/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { ReactElement, useState } from "react";
import { View } from "react-native";
import { NavLink } from "react-router-dom";
import ReactTooltip from "react-tooltip";

import { useSelector } from "react-redux";
import { RootState } from "store/types";

import { Text } from "UpduoLibrary";
import {
  skaleLinkBlue,
  skaleWebTextGray,
  colorWithOpacity,
  upduoPrimaryBlue,
  upduoBlueButtonHover,
} from "config/UpduoColors";
import * as UpduoAnalytics from "config/UpduoAnalytics";

interface ExploreLinkProps {
  content?: ReactElement;
  isIncomplete?: boolean;
  text?: string;
  to: string;
  tooltipId?: string;
  blue?: boolean;
  hasTooltip?: boolean;
  callback?: () => void;
}

export default function ExploreLink({
  content,
  isIncomplete,
  text,
  to,
  tooltipId,
  blue = false,
  hasTooltip = false,
  callback,
}: ExploreLinkProps) {
  // Link to expected group or user's dashboard
  const authToken = useSelector((state: RootState) => state.authToken);

  const [isHovering, setIsHovering] = useState(false);

  if (!content) {
    const textColor = blue
      ? upduoPrimaryBlue
      : colorWithOpacity(skaleWebTextGray, isIncomplete ? 0.5 : 1);

    const hoverColor = blue ? upduoBlueButtonHover : skaleLinkBlue;

    const textContent = (
      <Text
        style={{
          flex: 1,
          flexWrap: "wrap",
          width: "100%",
          color: isHovering ? hoverColor : textColor,
        }}
      >
        {text}
      </Text>
    );

    content = hasTooltip ? (
      <View>
        <ReactTooltip id={`textTip-${text}`} type={"dark"} effect={"solid"}>
          {text}
        </ReactTooltip>
        <span
          style={{
            width: "100px",
            display: "inline-block",
            wordBreak: "keep-all",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          data-tip
          data-for={`textTip-${text}`}
        >
          {textContent}
        </span>
      </View>
    ) : (
      textContent
    );
  }

  if (
    to.includes("undefined") ||
    to.includes("null") ||
    to.includes("example")
  ) {
    return content;
  }

  return (
    <NavLink
      data-tip
      data-for={tooltipId}
      to={to}
      style={{
        textDecoration: "none",
      }}
      onMouseEnter={() => {
        setIsHovering(true);
      }}
      onMouseLeave={() => {
        setIsHovering(false);
      }}
      onClick={(e) => {
        if (!authToken) {
          e.preventDefault();
        }
        UpduoAnalytics.logEvent("user-dashboard-explore", {
          to: to,
          tooltipId: tooltipId,
          type: "text",
        });
        if (callback && typeof callback === "function") {
          callback();
        }
      }}
    >
      {content}
    </NavLink>
  );
}
